/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  FC,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useMemo,
  useRef,
} from "react";
import { AwvInfo, ResultTaskList } from "client";
import { Multiple } from "./Multiple";
import { ErrorDialog } from "components/Common/ErrorDialog";
import { useTranslation } from "react-i18next";
import { TimeoutError } from "exception/TimeoutError";
import { SystemErrorDialog } from "components/Common/SystemErrorDialog";
import { Box } from "@mui/material";
import { useDayJob } from "hooks/GetDayJobHook";
import moment from "moment";
import { getJobCardStatus } from "./JobCard";
import { SpinnerDelay } from "components/Common/SpinnerDelay";
import { useUserContext } from "context/UserContext";
import { useInterval } from "react-use";
import { Const } from "const";

interface Props {
  awvInfo: AwvInfo;
  setRunningTask: Dispatch<SetStateAction<ResultTaskList | undefined>>;
}

export const MultipleSingle: FC<Props> = ({ awvInfo, setRunningTask }) => {
  const { t } = useTranslation();
  const { roleName } = useUserContext();
  const [today, setDate] = useState(moment());
  const { loading, error, value, retry } = useDayJob(today, awvInfo);
  const valueRef = useRef(value?.taskInfos);

  const [showError, setShowError] = useState(false);
  const [first, setFirst] = useState(true);

  useInterval(() => {
    if (!loading) {
      retry();
    }
  }, Const.taskInterval);

  useEffect(() => {
    setFirst(true);
  }, [awvInfo]);
  useEffect(() => {
    if (!loading) {
      if (error) {
        setFirst(false);
        setShowError((pre) => pre || first);
      }
      if (value?.taskInfos) {
        setFirst(false);
        setRunningTask(value?.taskInfos?.find((task) => task.taskStatus === 2));
      }
      if (first || value?.taskInfos) {
        valueRef.current = value?.taskInfos;
      }
    }
  }, [loading, error, first, value?.taskInfos, setRunningTask]);

  const excludeHiddenTask = useMemo(() => {
    const tasks = value?.taskInfos ?? valueRef.current;
    return tasks?.filter(
      (task) =>
        getJobCardStatus(
          task.jobSetStatus,
          task.taskStatus,
          task.endDatetime,
          task.taskKind,
          roleName
        ) !== "hidden"
    );
  }, [roleName, value]);

  const isTimeout = error && error instanceof TimeoutError;
  return (
    <>
      <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
        <Multiple
          awvInfos={[awvInfo]}
          tasks={excludeHiddenTask}
          date={today}
          setDate={setDate}
          canChangeDate={false}
        />
      </Box>
      {first && loading && <SpinnerDelay />}
      {isTimeout ? (
        <ErrorDialog
          open={showError}
          message={t("AWVUI-MSG83")}
          positiveButtonText={t("AWVUI-MSG11")}
          onPositiveButtonClick={() => {
            setShowError(false);
          }}
        />
      ) : (
        <SystemErrorDialog
          open={showError}
          error={error}
          onClose={() => {
            setShowError(false);
          }}
        />
      )}
    </>
  );
};
